import { useEffect, useMemo, useState } from "react";
import omit from "lodash/fp/omit";
import classNames from "classnames";
import TableViewToggles, { type TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import { SCREEN_SM } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import useTableSelection from "@rio-cloud/rio-uikit/useTableSelection";
import LoadMoreButton from "@rio-cloud/rio-uikit/LoadMoreButton";
import Toolbar, { ColumnDetailsMap, ColumnLabel } from "./Toolbar";
import Table from "./Table";
import { FuelingRecord } from "./mock";
import { useT } from "@hooks";
import { useStoredTableSettings } from "@hooks/useStoredTableSettings";
import { useStoredTableSort } from "@hooks/useStoredTableSort";
import { buildTableExportLayout, type TableExportLayout } from "@utils/tableExport";

const defaultColumnOrder = ["date", "driver", "vehicle", "location", "fuelType", "quantity", "amount"];

const disabledColumns = [""];

const demoColumnsDetails: ColumnDetailsMap = {
  driver: {
    width: 230,
  },
};

const sortKeyMap: Record<string, keyof FuelingRecord | [keyof FuelingRecord, keyof FuelingRecord]> = {
  date: "sortableDate" as keyof FuelingRecord,
  amount: "sortableAmount" as keyof FuelingRecord,
};

export type TableFuelingProps = {
  viewType: TableViewTogglesViewType;
  data: FuelingRecord[];
  totalData: number;
  onLoadMore: () => void;
  handleRefresh: () => void;
  handleDownload: (layout: TableExportLayout) => void;
  onViewDetail?: (id: string) => void;
  isLoadingMore?: boolean;
  isLoadingTable?: boolean;
};

type ExtendedFuelingRecord = FuelingRecord & {
  sortableDate?: string;
  sortableAmount?: number;
};

type FuelingSortKey = Extract<keyof ExtendedFuelingRecord, string>;

const FUELING_SORT_KEYS = Array.from(
  new Set([...defaultColumnOrder, ...Object.values(sortKeyMap).filter((key) => typeof key === "string")])
) as FuelingSortKey[];

const TableFueling = (props: TableFuelingProps) => {
  const t = useT();

  const columnLabels: ColumnLabel = {
    date: t("table.column.dateTime"),
    driver: t("table.column.driver"),
    vehicle: t("table.column.vehicle"),
    location: t("table.column.location"),
    fuelType: t("table.column.fuelType"),
    quantity: t("table.column.quantity"),
    amount: t("table.column.amountPaid"),
  };

  const {
    viewType: externalViewType,
    data,
    totalData,
    onLoadMore,
    handleDownload,
    handleRefresh,
    onViewDetail,
    isLoadingMore = false,
    isLoadingTable = false,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);

  const {
    columnOrder,
    hiddenColumns,
    columnsDetails,
    viewType,
    handleColumnChange,
    handleColumnDetailsChange,
    handleViewTypeChange,
  } = useStoredTableSettings<ColumnDetailsMap>({
    storageKey: "fueling",
    defaultColumnOrder,
    defaultColumnsDetails: demoColumnsDetails,
    defaultViewType: externalViewType || TableViewToggles.VIEW_TYPE_TABLE,
    forcedViewType: window.innerWidth < SCREEN_SM ? TableViewToggles.VIEW_TYPE_SINGLE_CARD : undefined,
  });

  const transformDate = (dateString: string) => {
    const parts = dateString.split(" - ");
    const datePart = parts[0];
    const timePart = parts[1] || "00:00";
    const [day, month, year] = datePart.split("/");
    return `${year}-${month}-${day}T${timePart}`;
  };

  const transformAmount = (amountString: string) => {
    const cleaned = amountString
      .replace(/R\$\s?/g, "")
      .replaceAll(".", "")
      .replace(",", ".");
    return Number.parseFloat(cleaned) || 0;
  };

  const transformedData: ExtendedFuelingRecord[] = data.map((item) => ({
    ...item,
    sortableDate: transformDate(item.date),
    sortableAmount: transformAmount(item.amount),
  }));

  const {
    sortKey: localSortKey,
    sortDirection: localSortDirection,
    setSortKey: setLocalSortKey,
    setSortDirection: setLocalSortDirection,
  } = useStoredTableSort<FuelingSortKey>({
    storageKey: "fueling",
    allowedSortKeys: FUELING_SORT_KEYS,
    defaultSortKey: "sortableDate",
    defaultSortDirection: "desc",
  });

  const sortedData = useMemo(() => {
    if (!localSortKey) return transformedData;

    return [...transformedData].sort((a, b) => {
      const aValue = a[localSortKey];
      const bValue = b[localSortKey];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return localSortDirection === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return localSortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [transformedData, localSortKey, localSortDirection]);

  const { tableRef, updateRowSelection, onActiveRowChange } = useTableSelection<FuelingRecord>({
    tableData: data,
    idKey: "id" as keyof FuelingRecord,
  });

  useEffect(() => updateRowSelection(), [searchValue, updateRowSelection]);

  const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleSearchValueChange = (newSearchValue: string) => setSearchValue(newSearchValue);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.dataset.sortby;

    if (newSortBy) {
      const newSortKey = sortKeyMap[newSortBy] || newSortBy;

      if (newSortKey === localSortKey) {
        if (localSortDirection === "asc") {
          setLocalSortDirection("desc");
        } else {
          setLocalSortKey(null);
        }
      } else {
        setLocalSortKey(newSortKey as FuelingSortKey);
        setLocalSortDirection("asc");
      }
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setLocalSortKey(newSortBy as FuelingSortKey);
    setLocalSortDirection(newSortDir);
  };

  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = sortedData.map((record) => ({ ...withoutHidden(record) })) as FuelingRecord[];

  const searchResult = useMemo(() => {
    if (!searchValue) {
      return filteredRows;
    }
    return filteredRows.filter((row: Partial<FuelingRecord>) =>
      columns.some((col) => {
        const value = row[col as keyof FuelingRecord];
        if (value == null) {
          return false;
        }
        return String(value).toLowerCase().includes(searchValue.toLowerCase());
      })
    );
  }, [searchValue, filteredRows, columns]);

  const rows = searchResult;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_TABLE && "min-width-500",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === localSortKey,
      disabled: false,
    };
  });

  return (
    <div className="height-100pct">
      <Toolbar
        searchValue={searchValue}
        handleSearchValueChange={handleSearchValueChange}
        viewType={viewType}
        handleViewTypeChange={handleViewTypeChange}
        handleToggleTableSettingsDialog={handleToggleTableSettingsDialog}
        showTableSettingsDialog={showTableSettingsDialog}
        handleColumnChange={handleColumnChange}
        defaultColumnOrder={defaultColumnOrder}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        columnLabels={columnLabels}
        disabledColumns={disabledColumns}
        columnsDetails={columnsDetails}
        handleColumnDetailsChange={handleColumnDetailsChange}
        handleDownload={() =>
          handleDownload(
            buildTableExportLayout(columnOrder, hiddenColumns, localSortKey, localSortDirection, sortKeyMap)
          )
        }
        handleRefresh={handleRefresh}
      />
      {localSortKey && !isViewTypeTable && (
        <TableCardsSorting
          className="display-none display-flex-sm align-items-center"
          selectOptions={cardSortingSelectOptions}
          sortName={localSortKey as string}
          sortOrder={localSortDirection}
          onSortChange={handleCardSortChange}
        />
      )}
      <Table
        rows={rows}
        isViewTypeTable={isViewTypeTable}
        tableRef={tableRef}
        tableClassNames={tableClassNames}
        columns={columns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        sortKey={localSortKey as keyof FuelingRecord}
        sortDirection={localSortDirection}
        handleSortChange={handleSortChange}
        onActiveRowChange={onActiveRowChange}
        onViewDetail={onViewDetail}
        isLoading={isLoadingTable}
      />

      {rows?.length > 0 ? (
        <LoadMoreButton
          loaded={rows.length}
          total={totalData}
          onLoadMore={onLoadMore}
          loadMoreMessage={isLoadingMore ? "Carregando..." : "Carregar mais"}
          noMoreMessage={t("tolls.table.loadMore.noMore")}
          isInteractive={!isLoadingMore}
        />
      ) : null}
    </div>
  );
};

export default TableFueling;
